import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { adminApi } from '../api/admin';
import { Button } from '../components/UI/Button';
import { Input } from '../components/UI/Input';
import { Spinner } from '../components/UI/Spinner';
import type { UserSummary, AdminAccountDetailResponse, UserStatusFilter } from '../types/admin';

export function UsersTab() {
  const { showToast } = useToast();
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<UserStatusFilter>('all');
  const [approvingId, setApprovingId] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await adminApi.listUsers(filter);
      setUsers(response.users);
      setTotal(response.total);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to load users', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [filter, showToast]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleApprove = async (userId: string) => {
    try {
      setApprovingId(userId);
      const response = await adminApi.approveUser(userId);
      showToast(response.message, 'success');
      // Update user in list
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, approved: true } : u));
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to approve user', 'error');
    } finally {
      setApprovingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex items-center space-x-2">
        <label className="text-sm font-medium text-gray-700">Status:</label>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as UserStatusFilter)}
          className="rounded-md border-gray-300 shadow-sm text-sm focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="all">All Users</option>
          <option value="pending">Pending Approval</option>
          <option value="approved">Approved</option>
        </select>
        <span className="text-sm text-gray-500">({total} total)</span>
      </div>

      {/* Users table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No users found.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.approved ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Approved
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.is_superadmin ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Superadmin
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">User</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {!user.approved && (
                      <Button
                        variant="primary"
                        isLoading={approvingId === user.id}
                        onClick={() => handleApprove(user.id)}
                      >
                        Approve
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AccountsTab() {
  const { showToast } = useToast();
  const [searchId, setSearchId] = useState('');
  const [account, setAccount] = useState<AdminAccountDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditDescription, setCreditDescription] = useState('');
  const [isCreditLoading, setIsCreditLoading] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchId.trim()) return;

    try {
      setIsLoading(true);
      const result = await adminApi.getAccount(searchId.trim());
      setAccount(result);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Account not found', 'error');
      setAccount(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCredit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!account || !creditAmount) return;

    const amount = parseFloat(creditAmount);
    if (isNaN(amount) || amount <= 0) {
      showToast('Amount must be a positive number', 'error');
      return;
    }

    try {
      setIsCreditLoading(true);
      const result = await adminApi.creditAccount(
        account.id,
        creditAmount,
        creditDescription || undefined,
      );
      showToast(`Credited $${result.amount}. New cash: $${result.new_cash}`, 'success');
      setCreditAmount('');
      setCreditDescription('');
      // Reload account to show updated balances
      const updated = await adminApi.getAccount(account.id);
      setAccount(updated);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to credit account', 'error');
    } finally {
      setIsCreditLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Search */}
      <form onSubmit={handleSearch} className="flex items-end space-x-3">
        <div className="flex-1">
          <Input
            label="Account UUID"
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            placeholder="Enter account UUID..."
            disabled={isLoading}
          />
        </div>
        <Button type="submit" isLoading={isLoading}>
          Search
        </Button>
      </form>

      {/* Account Details */}
      {account && (
        <div className="space-y-6">
          {/* Account Summary */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Account Details</h3>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Account ID</dt>
                <dd className="text-sm text-gray-900 font-mono">{account.account_id}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd>
                  {account.active ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Inactive</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Cash</dt>
                <dd className="text-sm text-gray-900 font-mono">${account.cash}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Buying Power</dt>
                <dd className="text-sm text-gray-900 font-mono">${account.buying_power}</dd>
              </div>
            </dl>
          </div>

          {/* Credit Form */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Credit Account</h3>
            <form onSubmit={handleCredit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Amount"
                  type="number"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0.01"
                  disabled={isCreditLoading}
                />
                <Input
                  label="Description (optional)"
                  value={creditDescription}
                  onChange={(e) => setCreditDescription(e.target.value)}
                  placeholder="Initial funding"
                  disabled={isCreditLoading}
                />
              </div>
              <Button type="submit" isLoading={isCreditLoading}>
                Credit Account
              </Button>
            </form>
          </div>

          {/* Positions */}
          {account.positions.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Positions ({account.positions.length})</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Symbol</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Cost</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unrealized P&L</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Realized P&L</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {account.positions.map((pos) => (
                      <tr key={pos.id}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{pos.symbol}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono">{pos.quantity}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono">${pos.average_cost}</td>
                        <td className="px-4 py-3 text-sm font-mono">
                          <span className={parseFloat(pos.unrealized_pnl) >= 0 ? 'text-green-600' : 'text-red-600'}>
                            ${pos.unrealized_pnl}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-mono">
                          <span className={parseFloat(pos.realized_pnl) >= 0 ? 'text-green-600' : 'text-red-600'}>
                            ${pos.realized_pnl}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Balance Changes */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Balance Changes ({account.balance_changes.length})</h3>
            {account.balance_changes.length === 0 ? (
              <p className="text-sm text-gray-500">No balance changes recorded.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {account.balance_changes.map((bc) => (
                      <tr key={bc.id}>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(bc.created_at)}</td>
                        <td className="px-4 py-3 text-sm font-mono">
                          <span className={parseFloat(bc.amount) >= 0 ? 'text-green-600' : 'text-red-600'}>
                            ${bc.amount}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{bc.description || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

type AdminTab = 'users' | 'accounts';

export function Admin() {
  const [activeTab, setActiveTab] = useState<AdminTab>('users');

  // Note: Superadmin check is handled by ProtectedRoute in App.tsx
  // No need for redundant check here

  return (
    <Layout>
      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
          <p className="mt-1 text-sm text-gray-500">Manage users and accounts</p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('users')}
              className={`pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'users'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Users
            </button>
            <button
              onClick={() => setActiveTab('accounts')}
              className={`pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'accounts'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Accounts
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'users' ? <UsersTab /> : <AccountsTab />}
      </div>
    </Layout>
  );
}
